import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { CertificateService } from 'src/app/services/certificate.service';
import { NationalRegisterService } from 'src/app/services/national-register.service';
import { AccessGroup, NumDict, StateService } from 'src/app/services/state.service';

@Component({
  selector: 'app-certificate-request-form',
  templateUrl: './certificate-request-form.component.html',
  styleUrls: ['./certificate-request-form.component.css']
})
export class CertificateRequestFormComponent implements OnInit, OnDestroy {
  certForm: UntypedFormGroup;
  submitStarted = false;

  code: string;

  baseUrl: string;

  ssnNameLookup$: Observable<string>;
  vehicleAutocomplete$: Observable<string[]>;
  accessGroups: AccessGroup[] = [];
  accessGroup?: AccessGroup;
  stations: NumDict = {};
  districts: NumDict = {};
  
  certAccessFields: {[field: string]: string} = {
    vehicleCallsign: 'Bíl',
    phoneNumber: 'Síma',
    stationId: 'Stöð',
    districtId: 'Embætti'
  }

  lookupValid: boolean;

  sub: Subscription;

  @Output()
  submitComplete = new EventEmitter<boolean>();


  constructor(private fb: UntypedFormBuilder, private svc: CertificateService, private nrSvc: NationalRegisterService, public state: StateService) {
  }

  ngOnInit(): void {
    this.baseUrl = new URL(window.location.href).origin;

    this.reset();
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }

  reset() {
    const serviceTypes = Object.keys(this.state.user.serviceTypes);
    this.certForm = this.fb.group({
      certificateRecipientSsn: ['', Validators.compose([Validators.required, Validators.minLength(10), Validators.maxLength(10)])],
      accessType: [null],
      phoneNumber: [''],
      vehicleCallsign: [''],
      stationId: [null],
      districtId: [null],
      serviceTypeId: [null, Validators.required],
      accessGroupId: [null, Validators.required],
    });

    const serviceTypeIdCtrl = this.certForm.get('serviceTypeId');
    const vehicleCallsignCtrl = this.certForm.get('vehicleCallsign');
    const accessGroupIdCtrl = this.certForm.get('accessGroupId');
    
    if (this.sub) {
      this.sub.unsubscribe();
    }
    this.sub = serviceTypeIdCtrl.valueChanges.pipe(switchMap(x => 
      forkJoin([this.state.getAccessGroups(x), this.state.getStations(x), this.state.getDistricts(x)]))).subscribe(x => {
      this.accessGroups = x[0];
      this.stations = x[1];
      this.districts = x[2];

      if (x[0].length > 0 && !accessGroupIdCtrl.value) {
        accessGroupIdCtrl.setValue(x[0][0].id);
      }
      vehicleCallsignCtrl.updateValueAndValidity();
    });
    if (serviceTypes.length === 1) {
      serviceTypeIdCtrl.setValue(+serviceTypes[0]);
    }

    this.sub.add(accessGroupIdCtrl.valueChanges.subscribe(x => {
      const ag = this.accessGroups.find((v) => v.id === x);
      if (ag) {
        this.accessGroup = ag;
        for (const field of Object.keys(this.certAccessFields)) {
          const ctrl = this.certForm.get(field);
          if (ag.requiredFields.includes(field)) {
            ctrl.setValidators(Validators.required);
          } else {
            ctrl.clearValidators();
            if (!ag.conditionallyRequiredFields.includes(field) && !ag.optionalFields.includes(field)) {
              ctrl.reset();
            }
          }
          ctrl.updateValueAndValidity();
        }
        if (!ag.conditionallyRequiredFields || ag.conditionallyRequiredFields.length === 0) {
          this.certForm.get('accessType').reset();
        }
      }
    }));

    this.sub.add(this.certForm.get('accessType').valueChanges.subscribe(x => {
      for (const field of this.accessGroup?.conditionallyRequiredFields) {
        const ctrl = this.certForm.get(field);
        if (field === x) {
          ctrl.setValidators(Validators.required);
        } else {
          ctrl.reset();
          ctrl.clearValidators();
        }
        ctrl.updateValueAndValidity();
      }
    }));

    this.vehicleAutocomplete$ = this.certForm.get('vehicleCallsign').valueChanges.pipe(
      switchMap(x => this.state.getVehicles(serviceTypeIdCtrl.value).pipe(
        map(vehicles => vehicles.map(v => v.callsign)
            .filter(v => !x || v.toLocaleLowerCase().startsWith(x.toLocaleLowerCase()))))));

    this.ssnNameLookup$ = this.certForm.get('certificateRecipientSsn').valueChanges.pipe(
      switchMap((x: string) => {
        if ((x || '').length === 10) {
          return this.nrSvc.lookup(x).pipe(
            map(entry => entry?.name || 'Fannst ekki'),
            catchError((_) => 'Fannst ekki'));
        }
        return of('');
      }),
      tap((val) => this.lookupValid = !['Fannst ekki', ''].includes(val || ''))
    );
    this.submitStarted = false;
    this.code = '';
  }

  copyUrl() {
    navigator.clipboard.writeText(this.baseUrl + '/?key=' + this.code);
  }

  onSubmit() {
    this.certForm.markAllAsTouched();
    if (!this.certForm.valid || this.submitStarted) {
      return;
    }
    this.submitStarted = true;

    this.svc.issueCertRequest(this.certForm.getRawValue()).subscribe(x => {
      this.submitComplete.emit();
      this.certForm.markAsPristine();
      this.submitStarted = false;
      this.code = x.certificateRequestKey;
    },
    error => {
      this.submitStarted = false;
    });
  }

}
